import { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors } from "@/mocks/aiassist";
import { generalHints, timeZones } from "./general-meta";
import { useWorkspaceSettings, type Recipient, type SettingsPatch } from "./workspace-settings";
import { api, apiErrorText } from "@/lib/api";
import { goAnchor } from "./app-nav";
import { t, tn } from "@/lib/i18n";

/* Живой макет «Общие сведения» (General info) в настройках рабочего пространства:
   название, тайм-аут неактивности, часовой пояс и еженедельный дайджест с получателями. */

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "•";

/** «минута / минуты / минут» без числа — подпись у поля (tn даёт «5 минут», число отрезаем) */
const minutesWord = (n: number) => tn(n, "минута", "минуты", "минут").replace(/^-?\d+\s/, "");

/** Подсказка у поля: иконка «i», текст появляется по наведению и по фокусу. */
function Info({ text }: { text: string }) {
  return (
    <span className="group relative inline-flex align-middle">
      <button
        type="button"
        aria-label={text}
        className="grid size-4 cursor-help place-items-center rounded-full text-muted-foreground transition hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <i className="ri-information-line text-[13px] leading-none" aria-hidden />
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full start-1/2 z-40 mb-1.5 w-60 -translate-x-1/2 rounded-btn border border-border bg-card px-2.5 py-2 text-[11px] font-normal leading-relaxed text-muted-foreground opacity-0 shadow-lift transition group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

function GeneralSettingsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const ws = useWorkspaceSettings();
  const [name, setName] = useState("");
  const [timeoutValue, setTimeoutValue] = useState("");
  const [zone, setZone] = useState("Asia/Dubai");
  const [recap, setRecap] = useState(true);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  // кого можно добавить в получатели — активные пользователи пространства
  const [candidates, setCandidates] = useState<Recipient[]>([]);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  // данные с сервера → поля формы
  useEffect(() => {
    if (!ws.data) return;
    setName(ws.data.name);
    setTimeoutValue(String(ws.data.inactivityMinutes));
    setZone(ws.data.timezone);
    setRecap(ws.data.digestEnabled);
    setRecipients(ws.data.digestRecipients);
  }, [ws.data]);

  useEffect(() => {
    api<{ id: string; name: string; email: string; status: string }[]>("/users").then(
      (list) => setCandidates(list.filter((u) => u.status !== "disabled").map(({ id, name, email }) => ({ id, name, email }))),
      () => undefined,
    );
  }, []);

  useEffect(() => {
    if (!picking) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPicking(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPicking(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [picking]);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  };

  const onNav = (id: string) => {
    if (id === "general") return;
    if (id === "ai-assist") {
      goAnchor("#ai-assist");
      return;
    }
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{label}» в демо не открывается", { label: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const minutes = Number(timeoutValue);
  const timeoutValid = Number.isInteger(minutes) && minutes >= 1 && minutes <= 120;

  // каждое поле сохраняется сразу; если сервер не принял — поле возвращается к сохранённому значению
  const persist = async (patch: SettingsPatch, text: string) => {
    try {
      await ws.save(patch);
      flash(text);
      return true;
    } catch (err) {
      flash(apiErrorText(err));
      if (ws.data) {
        setName(ws.data.name);
        setTimeoutValue(String(ws.data.inactivityMinutes));
        setZone(ws.data.timezone);
        setRecap(ws.data.digestEnabled);
        setRecipients(ws.data.digestRecipients);
      }
      return false;
    }
  };

  const commitTimeout = () => {
    if (!timeoutValid || minutes === ws.data?.inactivityMinutes) return;
    void persist({ inactivityMinutes: minutes }, t("Тайм-аут неактивности сохранён: {n}", { n: tn(minutes, "минута", "минуты", "минут") }));
  };

  const pickable = useMemo(() => {
    const q = query.trim().toLowerCase();
    return candidates
      .filter((c) => !recipients.some((r) => r.id === c.id))
      .filter((c) => (q ? `${c.name} ${c.email}`.toLowerCase().includes(q) : true));
  }, [candidates, recipients, query]);

  const addRecipient = (person: Recipient) => {
    const next = [...recipients, person];
    setRecipients(next);
    setQuery("");
    setPicking(false);
    void persist({ digestRecipients: next.map((r) => r.id) }, t("{name} будет получать еженедельный дайджест", { name: person.name }));
  };

  const removeRecipient = (person: Recipient) => {
    const next = recipients.filter((r) => r.id !== person.id);
    setRecipients(next);
    void persist({ digestRecipients: next.map((r) => r.id) }, t("{name} больше не получает дайджест", { name: person.name }));
  };

  return (
    <Band id="general-settings" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("рабочее пространство → общие сведения")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Часовой пояс: {zone}", { zone })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "general";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Общие сведения меняют владелец и администраторы рабочего пространства.")}
              </p>
            </div>
          </div>

          {/* ── содержимое окна ── */}
          <div className="min-w-0">
            <div className="flex gap-1.5 overflow-x-auto border-b border-border px-3.5 py-2 lg:hidden">
              {aiNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "general" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="p-3.5 md:p-5">
              <div className="flex flex-wrap items-start gap-2.5">
                <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                  <i className="ri-information-line text-base leading-none" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold leading-snug">{t("Общие сведения")}</h3>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    {t("Изменяйте общие настройки рабочего пространства.")}
                  </p>
                </div>
              </div>

              <div className="mt-5 flex max-w-xl flex-col gap-4">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12px] font-medium text-muted-foreground">{t("Название рабочего пространства")}</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => name.trim() && name.trim() !== ws.data?.name && void persist({ name: name.trim() }, t("Название рабочего пространства сохранено"))}
                    maxLength={60}
                    placeholder={t("Например, отдел продаж")}
                    className="h-10 w-full rounded-btn border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  {name.trim().length === 0 ? (
                    <span className="text-[11px] text-red-600">{t("Без названия рабочее пространство не сохранить")}</span>
                  ) : null}
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground">
                    {t("Тайм-аут неактивности пользователя")}
                    <Info text={t("Сколько минут сотрудник может бездействовать, прежде чем система попросит войти заново. Допустимо от 1 до 120 минут.")} />
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      value={timeoutValue}
                      onChange={(e) => setTimeoutValue(e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
                      onBlur={commitTimeout}
                      inputMode="numeric"
                      aria-invalid={!timeoutValid}
                      className={cn(
                        "h-10 w-24 rounded-btn border bg-background px-3 text-[13px] tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20",
                        timeoutValid ? "border-border focus:border-primary" : "border-red-500/70",
                      )}
                    />
                    <span className="text-[12px] text-muted-foreground">{minutesWord(minutes || 1)}</span>
                  </div>
                  {!timeoutValid ? (
                    <span className="text-[11px] text-red-600">{t("Укажите целое число минут — от 1 до 120")}</span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">
                      {t("Через {n} бездействия сотрудник выходит из системы.", { n: tn(minutes, "минуту", "минуты", "минут") })}
                    </span>
                  )}
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground">
                    {t("Часовой пояс")}
                    <Info text={t("По этому поясу показываются время диалогов, рабочие часы и дата в дайджесте.")} />
                  </span>
                  <div className="relative">
                    <select
                      value={zone}
                      onChange={(e) => {
                        setZone(e.target.value);
                        void persist({ timezone: e.target.value }, t("Часовой пояс сохранён"));
                      }}
                      aria-label={t("Часовой пояс")}
                      className="h-10 w-full cursor-pointer appearance-none rounded-btn border border-border bg-background pe-9 ps-3 text-[13px] text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      {timeZones.map((tz) => (
                        <option key={tz.id} value={tz.id}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-base leading-none text-muted-foreground" aria-hidden />
                  </div>
                </label>
              </div>

              <div className="mt-5 max-w-xl rounded-btn border border-border bg-background/60 p-4">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-[13px] font-semibold leading-snug">{t("Еженедельный дайджест на почту")}</h4>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                      {t("Выберите, хотите ли получать краткую сводку по диалогам и результатам жизненного цикла каждый понедельник.")}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={recap}
                    aria-label={t("Еженедельный дайджест на почту")}
                    onClick={() => {
                      setRecap(!recap);
                      void persist({ digestEnabled: !recap }, recap ? t("Еженедельный дайджест выключен") : t("Еженедельный дайджест включён"));
                    }}
                    className={cn(
                      "relative inline-flex h-6 w-11 flex-none cursor-pointer items-center rounded-full border transition",
                      recap ? "border-primary bg-primary" : "border-border bg-muted",
                    )}
                  >
                    <span
                      className={cn(
                        "size-5 rounded-full bg-card shadow transition-all",
                        recap ? "ms-auto me-0.5" : "ms-0.5",
                      )}
                      aria-hidden
                    />
                  </button>
                </div>

                <div
                  className={cn(
                    "mt-4 flex flex-col gap-1.5 border-t border-border/70 pt-3 transition",
                    !recap && "pointer-events-none opacity-50",
                  )}
                >
                  <span className="text-[12px] font-medium text-muted-foreground">{t("Получатели")}</span>
                  <div className="flex flex-wrap items-center gap-1.5" ref={pickerRef}>
                    {recipients.map((r) => (
                      <span
                        key={r.id}
                        title={r.email}
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-0.5 pe-1 ps-0.5 text-[11.5px] font-medium"
                      >
                        <span className="grid size-5 place-items-center rounded-full bg-primary/15 text-[8px] font-bold text-primary" aria-hidden>
                          {initials(r.name)}
                        </span>
                        {r.name}
                        <button
                          type="button"
                          onClick={() => removeRecipient(r)}
                          aria-label={t("Убрать {name} из получателей", { name: r.name })}
                          className="grid size-4 cursor-pointer place-items-center rounded-full text-muted-foreground transition hover:bg-red-500/10 hover:text-red-600"
                        >
                          <i className="ri-close-line text-[12px] leading-none" aria-hidden />
                        </button>
                      </span>
                    ))}

                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setPicking((v) => !v);
                          setQuery("");
                        }}
                        disabled={!recap}
                        aria-label={t("Добавить получателя")}
                        aria-expanded={picking}
                        className="grid size-6 cursor-pointer place-items-center rounded-full border border-border bg-card text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                      >
                        <i className="ri-add-line text-[13px] leading-none" aria-hidden />
                      </button>

                      {picking ? (
                        <div className="absolute start-0 top-[calc(100%+6px)] z-40 w-72 overflow-hidden rounded-btn border border-border bg-card shadow-lift">
                          <div className="border-b border-border p-2">
                            <input
                              autoFocus
                              value={query}
                              onChange={(e) => setQuery(e.target.value)}
                              placeholder={t("Поиск по имени или почте")}
                              aria-label={t("Поиск получателей")}
                              className="h-8 w-full rounded-btn border border-border bg-background px-2.5 text-[12px] placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                            />
                          </div>
                          <ul className="max-h-52 overflow-y-auto p-1">
                            {pickable.length === 0 ? (
                              <li className="px-2.5 py-3 text-[11.5px] text-muted-foreground">
                                {query.trim() ? t("Никого не нашли — измените запрос") : t("Все сотрудники уже получают дайджест")}
                              </li>
                            ) : (
                              pickable.map((p) => (
                                <li key={p.id}>
                                  <button
                                    type="button"
                                    onClick={() => addRecipient(p)}
                                    className="flex w-full cursor-pointer items-center gap-2.5 rounded-btn px-2 py-1.5 text-start transition hover:bg-muted"
                                  >
                                    <span className="grid size-7 flex-none place-items-center rounded-full bg-foreground/[.06] text-[10px] font-bold text-foreground/70" aria-hidden>
                                      {initials(p.name)}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate text-[12.5px] font-semibold leading-snug">{p.name}</span>
                                      <span className="block truncate text-[10.5px] text-muted-foreground">{p.email}</span>
                                    </span>
                                    <i className="ri-add-line flex-none text-[13px] leading-none text-primary" aria-hidden />
                                  </button>
                                </li>
                              ))
                            )}
                          </ul>
                          {recipients.length > 0 ? (
                            <div className="border-t border-border px-2.5 py-2 text-[10.5px] text-muted-foreground">
                              {t("Получателей: {n}", { n: recipients.length })}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <span className="text-[10.5px] text-muted-foreground">
                    {recipients.length > 0
                      ? t("Дайджест придёт {n} в понедельник утром.", { n: tn(recipients.length, "получателю", "получателям", "получателям") })
                      : t("Пока не выбран ни один получатель — дайджест отправлять некуда.")}
                  </span>
                </div>
              </div>

              <div className="mt-4 grid gap-2 border-t border-border/60 pt-3 sm:grid-cols-3">
                {generalHints.map((h) => (
                  <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                    <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                      <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                      {h.title}
                    </p>
                    <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
                  </div>
                ))}
              </div>

              <p className="mt-3 text-[11px] text-muted-foreground">
                {ws.data && !ws.data.canEdit
                  ? t("Менять настройки рабочего пространства могут владелец и администраторы.")
                  : t("Изменения сохраняются сразу и действуют для всего рабочего пространства.")}
              </p>
            </div>

            {toast ? (
              <div
                role="status"
                className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
              >
                <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                {toast}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Band>
  );
}

export default GeneralSettingsDemo;